import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Plus, Eye } from "lucide-react";
import useSetting from '@/hooks/setting';
import { useNavigate } from 'react-router-dom';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

const ListSetting = () => {
    const navigate = useNavigate();
    const { settings, deleteSetting } = useSetting();

    const renderValue = (value: any) => {
        if (typeof value === 'boolean') {
            return value ? (
                <Badge variant="default">True</Badge>
            ) : (
                <Badge variant="destructive">False</Badge>
            );
        }
        if (typeof value === 'object') {
            return (
                <div className="max-w-[300px] truncate" title={JSON.stringify(value)}>
                    {JSON.stringify(value)}
                </div>
            );
        }
        return (
            <div className="max-w-[300px] truncate" title={value?.toString()}>
                {value?.toString()}
            </div>
        );
    };

    const renderDetailValue = (value: any) => {
        if (typeof value === 'boolean') {
            return value ? (
                <Badge variant="default">True</Badge>
            ) : (
                <Badge variant="destructive">False</Badge>
            );
        }
        if (typeof value === 'object') {
            return (
                <pre className="whitespace-pre-wrap break-words">
                    {JSON.stringify(value, null, 2)}
                </pre>
            );
        }
        return value?.toString();
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xl font-bold">Settings List</CardTitle>
                <Button
                    onClick={() => navigate('/setting/create-setting')}
                    className="flex items-center gap-2"
                >
                    <Plus className="h-4 w-4" />
                    Add New
                </Button>
            </CardHeader>
            <CardContent>
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[200px]">Key</TableHead>
                                <TableHead>Value</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="w-[100px] text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {settings?.map((setting) => (
                                <TableRow key={setting.key}>
                                    <TableCell className="font-medium">{setting.key}</TableCell>
                                    <TableCell>{renderValue(setting.value)}</TableCell>
                                    <TableCell>
                                        <div className="max-w-[300px] truncate" title={setting.description}>
                                            {setting.description}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Dialog>
                                                <DialogTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent className="max-w-2xl bg-white">
                                                    <DialogHeader>
                                                        <DialogTitle>Setting Details</DialogTitle>
                                                    </DialogHeader>
                                                    <ScrollArea className="max-h-[60vh]">
                                                        <div className="space-y-4 p-4">
                                                            <div>
                                                                <h3 className="font-semibold mb-2">Key</h3>
                                                                <p className="text-sm">{setting.key}</p>
                                                            </div>
                                                            <div>
                                                                <h3 className="font-semibold mb-2">Value</h3>
                                                                <div className="text-sm">
                                                                    {renderDetailValue(setting.value)}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <h3 className="font-semibold mb-2">Description</h3>
                                                                <p className="text-sm whitespace-pre-wrap">
                                                                    {setting.description || 'No description'}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <h3 className="font-semibold mb-2">Status</h3>
                                                                <div className="text-sm">
                                                                    {setting.isActive ? (
                                                                        <Badge variant="default">Active</Badge>
                                                                    ) : (
                                                                        <Badge variant="destructive">Inactive</Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </ScrollArea>
                                                </DialogContent>
                                            </Dialog>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => navigate(`/setting/update-setting/${setting.key}`)}
                                                className="h-8 w-8"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        variant="destructive"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent className="bg-white">
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            Are you sure you want to delete this setting?
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                        <AlertDialogAction
                                                            onClick={() => deleteSetting.mutate(setting.key)}
                                                        >
                                                            Delete
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};

export default ListSetting;
